import React, { useEffect, useRef, useState } from "react";
import styles from "./AddSaleOrderPanel.module.css";
import { FaEdit, FaTrashAlt, FaSyncAlt,FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import WebService from "../Services/WebService";
import CrossButton from "../Customs/Button/CrossButton";
import ChooseVariantModal from "../pages/Watchlist/ChooseVariantModal";
import { primaryProductImage } from "../Services/productImages";
import Swal from "sweetalert2";
import AsyncSelect from "react-select/async";
import { cartLineDelivery, cartLinePackaging, cartLineProductTotal } from "../Services/linePricing";
import OrderChargesEditor, { OrderCharge } from "./OrderChargesEditor";

// --- Interfaces ---
interface AddSaleOrderPanelProps {
  open: boolean;
  onClose: () => void;
  saleOrderId?: string | number;
  userId?: string | number;
  openedFromProceedToPay?: boolean;
}

interface CustomerApi {
  customer_id: number;
  username: string;
  mobile_number: string;
  email?: string;
  user_id: number;
  is_credit_allowed?: boolean;
  credit_limit?: number;
  address?: AddressApi[];
  company_name?: string;
}
interface CustomerOption {
  value: number;
  label: string;
  customer: CustomerApi;
}
interface AddressApi {
  address_id: number;
  address_type: string;
  address: string;
  landmark: string;
  complete_address: string;
  city: string;
  state: string;
  country: string;
}
interface CartItem {
  cart_item_id: number;
  product_id: number;
  product_type: string;
  quantity: number;
  height: number;
  width: number;
  height_margin: number;
  width_margin: number;
  color: string;
  is_back_design: boolean;
  door_config: boolean;
  wood_quality_id: string;
  packaging_type_id: number;
  description: string;
  unit_price?: number;
  packaging_per_unit?: number;
  delivery_per_unit?: number;
  // Backend-computed line amounts — the source of truth for money (see Services/linePricing).
  base_amount?: number | null;
  gst_amount?: number | null;
  packaging_amount?: number | null;
  delivery_amount?: number | null;
  total_price?: number | null;
  product?: any;
  [key: string]: any;
  images?: string | Array<{ file_name?: string; url?: string } | string>;
}
interface PaymentSummary {
  subtotal: number;
  delivery: number;
  packaging: number;
  total: number;
}
type PaymentMethod = "bank" | "qr" | "cash" | "credit" | "";

 const SHOP_URL = "/shop"; 
// --- Constants ---
const HARDCODED_BANK_DETAILS = {
  holder: "KUNAL ENTERPRISES",
  bank: "HDFC bank",
  acc: "50200030559948",
  ifsc: "HDFC0004199",
  addr: "HDFC BANK PALDA INDORE",
};
const HARDCODED_QR = "/Assets/qr-code.png";

const initialFields = {
  customer: null as CustomerOption | null,
  phone: "",
  email: "",
};

const AddSaleOrderPanel: React.FC<AddSaleOrderPanelProps> = ({
  open,
  onClose,
  saleOrderId,
  userId,
  openedFromProceedToPay,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const initialLoadRef = useRef(true); // prevent false "dirty" on first load

  const [fields, setFields] = useState(initialFields);
  const [, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [submitAttempt, setSubmitAttempt] = useState(false);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartLoading, setCartLoading] = useState(false);

  // Confirm / Pay on an order that already exists (estimate): its items live on the
  // sale order, not in the cart (checkout empties the cart), so they are shown read-only.
  const isExistingOrderPay = !!openedFromProceedToPay && !!saleOrderId;

  // unified "unsaved changes" flag
  const [orderDirty, setOrderDirty] = useState(false);
  const invalidateConfirmation = () => {
    if (!isExistingOrderPay) setOrderDirty(true);
  };

  // Existing order only: discount and per-line other charges, shown in the summary.
  // otherCharges = legacy per-door charges; orderCharges = the order's "Other Charges" list.
  const [orderExtras, setOrderExtras] = useState({ discount: 0, otherCharges: 0 });
  const [orderCharges, setOrderCharges] = useState<OrderCharge[]>([]);
  // New order: charges typed before Confirm, saved right after the order is created.
  const [draftCharges, setDraftCharges] = useState<OrderCharge[]>([]);
  // True once the summary shows the saved order's amounts (discount, charges, due).
  const [orderLoaded, setOrderLoaded] = useState(false);

  const [summary, setSummary] = useState<PaymentSummary>({
    subtotal: 0,
    delivery: 0,
    packaging: 0,
    total: 0,
  });

  const formatSizeIn = (h: unknown, w: unknown): string | null => {
    const height = Number(h);
    const width = Number(w);
    if (!Number.isFinite(height) || !Number.isFinite(width) || height <= 0 || width <= 0) return null;
    return `${height} x ${width} in`;
  };

  const getOrderSizeLabel = (it: CartItem): string | null => {
    if (typeof (it as any).order_size === "string" && String((it as any).order_size).trim()) {
      return String((it as any).order_size).trim();
    }
    return formatSizeIn((it as any).order_height ?? it.height, (it as any).order_width ?? it.width);
  };

  const getBillSizeLabel = (it: CartItem): string | null => {
    if (typeof (it as any).bill_size === "string" && String((it as any).bill_size).trim()) {
      return String((it as any).bill_size).trim();
    }
    return formatSizeIn((it as any).bill_height, (it as any).bill_width);
  };

  const [addresses, setAddresses] = useState<AddressApi[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<number | null>(null);

  // Checkbox: delivery address = billing address (default true)
  const [sameAsBilling, setSameAsBilling] = useState<boolean>(true);
  const [customSaved, setCustomSaved] = useState<boolean>(false);

  // New Delivery (Shipping) Address (frontend-only) + validation
  const [addForm, setAddForm] = useState({
    name: "",
    mobile_number: "",
    country: "",
    state: "",
    city: "",
    address: "",
    landmark: "",
  });
  const [addressSubmitAttempt, setAddressSubmitAttempt] = useState(false);

  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [hasLoadedCountries, setHasLoadedCountries] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const [orderId, setOrderId] = useState<string | number | null>(saleOrderId || null);

  const [notes, setNotes] = useState("");
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [selectedPay, setSelectedPay] = useState<PaymentMethod>("");
  const [utr, setUTR] = useState("");
  const [paymentFile, setPaymentFile] = useState<File | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [editingCartItem, setEditingCartItem] = useState<CartItem | null>(null);

  // CASH amount (supports partial payment)
  const [cashAmount, setCashAmount] = useState<string>("");
  const [isEditingShipping, setIsEditingShipping] = useState<boolean>(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const PLACEHOLDER_IMG = "/Assets/no-pictures.png";

  // derive true edit mode (guarantees PATCH)
  const isEditMode = !!saleOrderId && !openedFromProceedToPay;
  
  // --- Helper: Fetch Customer By userId (for Proceed to Pay) ---
  const fetchCustomerById = async (id: number | string) => {
    try {
      const res = await WebService.getAPI<any>({ action: `/v1/door-management/customers?user_id=${id}` });
      const userArr = res?.data?.data?.users || res?.data?.users || [];
      if (userArr.length > 0) {
        const c = userArr[0];
        const customerOption: CustomerOption = {
          value: c.user_id,
          label: c.username,
          customer: {
            ...c,
            email: c.email || "",
            is_credit_allowed: c.is_credit_allowed ?? false,
            credit_limit: c.credit_limit ?? 0,
            address: c.address ?? [],
          },
        };
        setFields((f) => ({
          ...f,
          customer: customerOption,
          phone: c.mobile_number || "",
          email: c.email || "",
        }));
        setCustomerOptions((opts) => (opts.find((o) => o.value === c.user_id) ? opts : [...opts, customerOption]));
      }
    } catch {
      toast.error("Failed to fetch customer details.");
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleDownloadEstimate = async () => {
    if (!orderId) {
      toast.error("Order not confirmed!");
      return;
    }
    setDownloading(true);
    try {
      const blob = await WebService.downloadFileAPI({
        action: `/v1/door-management/saleorders/${orderId}/estimations`,
        method: "GET",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Estimate_${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Estimate downloaded!");
    } catch {
      /* handled globally if needed */
    }
    setDownloading(false);
  };

  // Load an order's own items and amounts. replaceCart=false keeps the cart rows on screen
  // (new-order flow after Confirm) and only refreshes the summary from the saved order.
  const loadOrder = async (id: string | number, replaceCart: boolean) => {
    setCartLoading(true);
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/saleorders/${id}`,
      });
      const order = res?.data || {};
      const items: CartItem[] = (order.sale_order_items || []).map((it: any) => ({
        ...it,
        cart_item_id: it.sale_order_items_id,
      }));
      if (replaceCart) setCart(items);

      const subtotal = Number(order.total_amount) || 0;
      const finalAmount = Number(order.final_amount) || 0;
      const discount = Number(order.discount_amount) || 0;
      const otherCharges = items.reduce((sum, it: any) => sum + (Number(it.additional_charge) || 0), 0);
      const charges: OrderCharge[] = Array.isArray(order.other_charges) ? order.other_charges : [];
      const chargesTotal = charges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
      const delivery = items.reduce((sum, it) => sum + cartLineDelivery(it), 0);
      // Line items don't carry packaging; it is what remains of the final amount.
      const packaging = Math.max(
        0,
        Math.round((finalAmount - subtotal - delivery - otherCharges - chargesTotal + discount) * 100) / 100
      );
      const due = order.due_amount != null ? Number(order.due_amount) : finalAmount;
      setSummary({ subtotal, delivery, packaging, total: due });
      setOrderExtras({ discount, otherCharges });
      setOrderCharges(charges);

      setOrderLoaded(true);
      if (replaceCart) setNotes(order.notes || "");
      setOrderId(order.sale_order_id || id);
      setHasConfirmed(true);
      setOrderDirty(false);
    } catch {
      if (replaceCart) setCart([]);
    }
    setCartLoading(false);
    initialLoadRef.current = false;
  };

  // Confirm / Pay on an existing order.
  const loadExistingOrder = () => (saleOrderId ? loadOrder(saleOrderId, true) : Promise.resolve());

  // --- Reset state on open/close or new edit ---
  useEffect(() => {
    setFields(initialFields);
    setSubmitAttempt(false);

    setCart([]);
    setSummary({ subtotal: 0, delivery: 0, packaging: 0, total: 0 });

    setAddresses([]);
    setSelectedAddress(null);

    setSameAsBilling(true);
    setCustomSaved(false);
    setAddForm({
      name: "",
      mobile_number: "",
      country: "",
      state: "",
      city: "",
      address: "",
      landmark: "",
    });
    setAddressSubmitAttempt(false);

    setHasConfirmed(false);
    setSelectedPay("");
    setUTR("");
    setPaymentFile(null);
    setNotes("");
    setEditingCartItem(null);
    setCashAmount("");
    setOrderDirty(false); // clean slate when panel opens
    setOrderLoaded(false);
    setDraftCharges([]);
    setOrderCharges([]);
    setOrderExtras({ discount: 0, otherCharges: 0 });
    initialLoadRef.current = true;

    setOrderId(saleOrderId || null);

    // Only load an existing order if we are truly editing (NOT Proceed to Pay)
    if (open && saleOrderId && !openedFromProceedToPay) {
      (async () => {
        try {
          const res = await WebService.getAPI<any>({
            action: `/v1/door-management/saleorders?order_id_like=${saleOrderId}`,
          });
          const order = Array.isArray(res?.data?.sale_orders) ? res.data.sale_orders[0] : res.data;
          if (order?.customer?.user_id) {
            setFields({
              customer: {
                value: order.customer.user_id,
                label: order.customer.username,
                customer: order.customer,
              },
              phone: order.customer.mobile_number || "",
              email: order.customer.email || "",
            });
          }
          setCart(order.cart_items || []);
          setAddresses(order.addresses || []);
          setSelectedAddress(order.address_id || null);
          setNotes(order.notes || "");
          setHasConfirmed(order.status === "CHECKOUT" || false);
          setOrderId(order.sale_order_id || saleOrderId);
          setOrderDirty(false); 
          initialLoadRef.current = false;
        } catch {
          /* ignore */
          initialLoadRef.current = false;
        }
      })();
    } else if (open && saleOrderId && openedFromProceedToPay) {
      loadExistingOrder();
    } else {
      initialLoadRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, saleOrderId, openedFromProceedToPay]);

  const mapUsersToOptions = (arr: CustomerApi[]) =>
    arr
      .filter((c) => !!c.user_id)
      .map((c) => ({
        value: c.user_id,
        label: c.company_name ? `${c.company_name} (${c.username})` : c.username,
        customer: c,
      }));

  const loadCustomerOptions = async (inputValue: string) => {
    const qs = new URLSearchParams({
      page_number: "1",
      limit: "10",
      status: "ACTIVE",
      ...(inputValue.trim() ? { user_like: inputValue.trim() } : {}),
    });
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/customers?${qs.toString()}`,
      });
      const users: CustomerApi[] = res?.data?.data?.users || res?.data?.users || [];
      return mapUsersToOptions(users);
    } catch {
      return [];
    }
  };

  // If Proceed to Pay, auto-select customer
  useEffect(() => {
    if (openedFromProceedToPay && userId && !fields.customer) {
      fetchCustomerById(userId);
    }
    // eslint-disable-next-line
  }, [openedFromProceedToPay, userId]);

  useEffect(() => {
    if (fields.customer?.value) {
      setCustomSaved(false);
      setAddressSubmitAttempt(false);
      setSameAsBilling(true);
      setIsEditingShipping(false);
      if (!isExistingOrderPay) fetchCartByUserId(fields.customer.value);
      const seeded = fields.customer.customer?.address;
      if (Array.isArray(seeded) && seeded.length > 0) {
        applyAddressList(seeded);
      }
      fetchAddresses(fields.customer.customer.user_id);
      setFields((prev) => ({
        ...prev,
        phone: fields.customer?.customer?.mobile_number || "",
        email: fields.customer?.customer?.email || "",
      }));
      if (!initialLoadRef.current && !isEditMode && !isExistingOrderPay) {
        setOrderDirty(true);
      }
    } else {
      setCart([]);
      setSummary({ subtotal: 0, delivery: 0, packaging: 0, total: 0 });
      setAddresses([]);
      setSelectedAddress(null);
      setOrderDirty(false);
    }
    // eslint-disable-next-line
  }, [fields.customer]);

  // Fetch cart by USER_ID
  const fetchCartByUserId = async (userId: number) => {
    setOrderLoaded(false); // summary follows the cart again
    setCartLoading(true);
    try {
      const res = await WebService.getAPI<any>({ action: `/v1/door-management/user/${userId}/cart` });
      setCart(res.data || []);
      let subtotal = 0;
      let totalDelivery = 0;
      let totalPackaging = 0;
      (res.data || []).forEach((it: any) => {
        subtotal += cartLineProductTotal(it);
        totalPackaging += cartLinePackaging(it);
        totalDelivery += cartLineDelivery(it);
      });
      setSummary({
        subtotal,
        delivery: totalDelivery,
        packaging: totalPackaging,
        total: subtotal + totalDelivery + totalPackaging,
      });
    } catch {
      setCart([]);
      setSummary({ subtotal: 0, delivery: 0, packaging: 0, total: 0 });
    }
    setCartLoading(false);
  };

  const applyAddressList = (list: AddressApi[] | undefined | null) => {
    const addrs = Array.isArray(list) ? list : [];
    setAddresses(addrs);
    setSelectedAddress((prev) => {
      if (prev != null && addrs.some((a) => Number(a.address_id) === Number(prev))) {
        return prev;
      }
      return addrs[0]?.address_id ?? null;
    });
    if (addrs.length === 0) {
      setSameAsBilling(false);
      setIsEditingShipping(true);
      setCustomSaved(false);
      setAddForm((f) => ({
        ...f,
        name: f.name || fields.customer?.customer?.username || fields.customer?.label || "",
        mobile_number: f.mobile_number || fields.customer?.customer?.mobile_number || fields.phone || "",
      }));
    }
  };

  // Fetch addresses by user_id (saved/billing addresses).
  // Customer list payloads already include `address`; GET /users historically did not.
  const fetchAddresses = async (userId: number) => {
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/customers?user_id=${userId}`,
      });
      const userArr = res?.data?.data?.users || res?.data?.users || [];
      const fromCustomer = userArr[0]?.address;
      if (Array.isArray(fromCustomer)) {
        applyAddressList(fromCustomer);
        return;
      }
    } catch {
      /* fall through to /users */
    }
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/users?user_id=${userId}`,
      });
      const userArr = res?.data?.data?.users || res?.data?.users || [];
      applyAddressList(userArr[0]?.address);
    } catch {
      /* keep any addresses already applied from the selected customer */
    }
  };

  // product image resolve (always via API host + product-linked path)
  const getCartItemImgSrc = (it: CartItem): string => {
    const fromItem = primaryProductImage(it);
    if (fromItem && fromItem !== PLACEHOLDER_IMG) return fromItem;
    const fromProduct = primaryProductImage(it.product);
    if (fromProduct && fromProduct !== PLACEHOLDER_IMG) return fromProduct;
    return PLACEHOLDER_IMG;
  };

  // COUNTRY/STATE/CITY lists (custom shipping, or first address for a customer with none)
  useEffect(() => {
    if (!open) return;
    const needsGeo = !sameAsBilling || addresses.length === 0;
    if (needsGeo && !hasLoadedCountries) {
      WebService.getAPI<any>({ action: "/v1/door-management/users/countries" })
        .then((res) => {
          const rawList: any[] = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
          setCountries(
            rawList.map((c: any) => ({
              id: c.id ?? c.country_id ?? 0,
              name: c.name ?? c.country_name ?? "",
            }))
          );
          setHasLoadedCountries(true);
        })
        .catch(() => setCountries([]));
    }
  }, [open, sameAsBilling, addresses.length, hasLoadedCountries]);

  useEffect(() => {
    if (!addForm.country) {
      setStates([]);
      setAddForm((f) => ({ ...f, state: "", city: "" }));
      return;
    }
    WebService.getAPI<any>({ action: `/v1/door-management/users/countries/${addForm.country}/states` })
      .then((res) => {
        const rawList: any[] = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        setStates(
          rawList.map((s: any) => ({
            id: s.id ?? s.state_id ?? 0,
            name: s.name ?? s.state_name ?? "",
          }))
        );
        setAddForm((f) => ({ ...f, state: "", city: "" }));
      })
      .catch(() => {
        setStates([]);
        setAddForm((f) => ({ ...f, state: "", city: "" }));
      });
  }, [addForm.country]);

  useEffect(() => {
    if (!addForm.state) {
      setCities([]);
      setAddForm((f) => ({ ...f, city: "" }));
      return;
    }
    WebService.getAPI<any>({ action: `/v1/door-management/users/states/${addForm.state}/cities` })
      .then((res) => {
        const rawList: any[] = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        setCities(
          rawList.map((c: any) => ({
            id: c.id ?? c.city_id ?? 0,
            name: c.name ?? c.city_name ?? "",
          }))
        );
        setAddForm((f) => ({ ...f, city: "" }));
      })
      .catch(() => {
        setCities([]);
        setAddForm((f) => ({ ...f, city: "" }));
      });
  }, [addForm.state]);

  // Cart actions
  const handleDeleteCartItem = async (cart_item_id: number) => {
    if (!fields.customer?.value) return;
    const confirm = await Swal.fire({
      title: "Remove item?",
      text: "Are you sure you want to remove this item from cart?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await WebService.deleteAPI({ action: `/v1/door-management/user/${fields.customer.value}/cart/${cart_item_id}` });
      await fetchCartByUserId(fields.customer.value);
      invalidateConfirmation();
    } catch {
      toast.error("Failed to remove item.");
    }
  };

  const handleUpdateQuantity = async (item: CartItem, newQuantity: number) => {
    if (!fields.customer?.value) return;
    if (newQuantity < 1) return;
    const patchPayload = {
      cart_item_id: item.cart_item_id,
      product_id: item.product_id,
      product_type: item.product_type,
      quantity: newQuantity,
      height: item.height,
      width: item.width,
      height_margin: item.height_margin,
      width_margin: item.width_margin,
      mica_id: (item as any).mica_id,
      color: item.color,
      door_config: item.door_config,
      is_back_design: item.is_back_design,
      is_sagwan_bidding: (item as any).is_sagwan_bidding,
      is_back_puc: (item as any).is_back_puc,
      wood_quality_id: item.wood_quality_id,
      packaging_type_id: item.packaging_type_id,
      description: item.description,
      product_name:
        item.product_name || item.product?.product_name || item.product?.name || item.name || "",
      product_description:
        ("design_description" in (item.product || {}) && (item.product as any).design_description) ||
        item.product_description ||
        item.product?.product_description ||
        item.product?.description ||
        item.description ||
        "",
    };
    try {
      await WebService.patchAPI({
        action: `/v1/door-management/user/${fields.customer.value}/cart`,
        body: patchPayload,
      });
      await fetchCartByUserId(fields.customer.value);
      invalidateConfirmation();
    } catch {
      toast.error("Could not update quantity.");
    }
  };

  // Validation helpers (new delivery form)
  const mobileValid = (val: string) => /^\d{10,15}$/.test(val.trim());
  const fieldError = (val: string) => !val || !val.trim();

  const shippingAddressPayload = () => ({
    name: addForm.name.trim(),
    mobile_number: addForm.mobile_number.trim(),
    address: addForm.address.trim(),
    landmark: addForm.landmark.trim(),
    country: parseInt(addForm.country),
    state: parseInt(addForm.state),
    city: parseInt(addForm.city),
    country_id: parseInt(addForm.country),
    state_id: parseInt(addForm.state),
    city_id: parseInt(addForm.city),
  });

  const hasCustomShipping = !sameAsBilling && customSaved;
  const canConfirmOrder = !!fields.customer && (!!selectedAddress || hasCustomShipping);

  // Save custom delivery (shipping) address — persist for customers with none, otherwise keep for this order
  const handleSaveCustomAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddressSubmitAttempt(true);

    const hasErrors =
      fieldError(addForm.name) ||
      !mobileValid(addForm.mobile_number) ||
      fieldError(addForm.country) ||
      fieldError(addForm.state) ||
      fieldError(addForm.city) ||
      fieldError(addForm.address) ||
      fieldError(addForm.landmark);

    if (hasErrors) {
      toast.error("Please complete the delivery address.");
      return;
    }

    const userId = fields.customer?.customer?.user_id ?? fields.customer?.value;
    const isFirstAddress = addresses.length === 0;

    if (userId && isFirstAddress) {
      setSavingAddress(true);
      try {
        const res = await WebService.putAPI<any>({
          action: `/v1/door-management/customers/address`,
          body: {
            user_id: userId,
            address_type: "GENERAL",
            address: addForm.address.trim(),
            landmark: addForm.landmark.trim(),
            country: parseInt(addForm.country),
            state: parseInt(addForm.state),
            city: parseInt(addForm.city),
          },
        });
        const saved = res?.data || {};
        const newAddr: AddressApi = {
          address_id: saved.address_id,
          address_type: saved.address_type || "GENERAL",
          address: saved.address || addForm.address.trim(),
          landmark: saved.landmark || addForm.landmark.trim(),
          complete_address: saved.complete_address || saved.address || addForm.address.trim(),
          city: String(saved.city ?? addForm.city),
          state: String(saved.state ?? addForm.state),
          country: String(saved.country ?? addForm.country),
        };
        if (newAddr.address_id) {
          applyAddressList([newAddr]);
          setSameAsBilling(true);
        }
        setCustomSaved(true);
        setIsEditingShipping(false);
        toast.success("Address saved. You can confirm the order.");
        invalidateConfirmation();
        return;
      } catch {
        // Fall through: still allow Confirm with this order-only shipping address
      } finally {
        setSavingAddress(false);
      }
    }

    setCustomSaved(true);
    setIsEditingShipping(false);
    toast.success("Delivery address saved. It will be sent on Confirm.");
  };

  // Confirm Order (POST/PATCH) — with billing+shipping payload
  const handleConfirmOrder = async () => {
    setSubmitAttempt(true);

    if (!fields.customer) return toast.error("Select customer!");
    if (!selectedAddress && !hasCustomShipping) {
      return toast.error("Select a billing address or save a shipping address!");
    }

    if (!sameAsBilling) {
      setAddressSubmitAttempt(true);
      const hasErrors =
        fieldError(addForm.name) ||
        !mobileValid(addForm.mobile_number) ||
        fieldError(addForm.country) ||
        fieldError(addForm.state) ||
        fieldError(addForm.city) ||
        fieldError(addForm.address) ||
        fieldError(addForm.landmark);
      if (hasErrors) return toast.error("Please complete the delivery address.");
    }

    const customPayload = !sameAsBilling ? shippingAddressPayload() : null;

    try {
      let res;
      if (isEditMode) {
        // EDIT (PATCH)
        const base: any = {
          customer_id: fields.customer.customer.customer_id,
          user_id: fields.customer.value,
          notes,
          ...(selectedAddress ? { address_id: selectedAddress } : {}),
        };
        const body = sameAsBilling
          ? { ...base, use_custom_billing: false, use_custom_shipping: false }
          : {
              ...base,
              use_custom_billing: true,
              billing_address: customPayload,
              use_custom_shipping: true,
              shipping_address: customPayload,
            };
        res = await WebService.patchAPI({
          action: `/v1/door-management/sale-orders/${saleOrderId}`,
          body,
        });
        toast.success("Order updated!");
      } else {
        // NEW (POST) — Proceed to Pay path always lands here
        const body = sameAsBilling
          ? {
              ...(selectedAddress ? { address_id: selectedAddress } : {}),
              notes,
              use_custom_billing: false,
              use_custom_shipping: false,
            }
          : {
              ...(selectedAddress ? { address_id: selectedAddress } : {}),
              notes,
              use_custom_billing: true,
              billing_address: customPayload,
              use_custom_shipping: true,
              shipping_address: customPayload,
            };

        res = await WebService.postAPI({
          action: `/v1/door-management/user/${fields.customer.value}/cart/checkout`,
          body,
        });
        toast.success("Order confirmed. Proceed to payment!");
      }

      const id =
        (res as any)?.sale_order_id ??
        (res as any)?.data?.sale_order_id ??
        (res as any)?.data?.data?.sale_order_id ??
        (res as any)?.id ??
        null;

      setOrderId(id);
      setHasConfirmed(true);
      setOrderDirty(false); // clean again after successful confirm/save

      if (!isEditMode && id) {
        if (draftCharges.length > 0) {
          try {
            await WebService.putAPI({
              action: `/v1/door-management/saleorders/${id}/other-charges`,
              body: { charges: draftCharges },
            });
            setDraftCharges([]);
          } catch {
            toast.error("Order created, but other charges were not saved. Add them again below.");
          }
        }
        await loadOrder(id, false);
      }
    } catch (e: any) {
      toast.error(e?.message || "Could not save order.");
    }
  };

  // Payment Checkout (bank/qr/cash/credit)
  const handlePaymentCheckout = async () => {
    if (!orderId) return toast.error("Order not confirmed. Please confirm first.");
    if (!selectedPay) return toast.error("Please select payment method!");
    if (selectedPay === "bank" && !utr) return toast.error("Please enter UTR number!");
    if ((selectedPay === "bank" || selectedPay === "qr") && !paymentFile) {
      toast.error("Please upload a payment screenshot!");
      return;
    }
    if (selectedPay === "cash" && (!cashAmount || Number(cashAmount) < 1)) {
      toast.error("Please enter a valid cash amount!");
      return;
    }

    const userIdVal = fields.customer?.value;
    if (!userIdVal) {
      toast.error("No customer selected.");
      setCheckoutLoading(false);
      return;
    }

    setCheckoutLoading(true);
    try {
      const payload: any = {
        payment_method:
          selectedPay === "bank"
            ? "BANK_TRANSFER"
            : selectedPay === "qr"
            ? "UPI"
            : selectedPay === "cash"
            ? "CASH"
            : selectedPay === "credit"
            ? "CREDIT"
            : "",
        sale_order_id: orderId?.toString() || "",
        notes: notes || "",
      };
      if (selectedPay === "bank") {
        payload.transaction_id = utr;
        payload.amount = summary.total.toString();
      } else if (selectedPay === "qr") {
        payload.transaction_id = "QR_PAYMENT";
        payload.amount = summary.total.toString();
      } else if (selectedPay === "credit") {
        payload.transaction_id = "CREDIT_PAYMENT";
        payload.amount = summary.total.toString();
      } else if (selectedPay === "cash") {
        payload.amount = cashAmount;
      }

      const formData = new FormData();
      Object.entries(payload).forEach(([key, val]) => {
        if (val !== undefined && val !== null) formData.append(key, val as string);
      });
      if ((selectedPay === "bank" || selectedPay === "qr") && paymentFile) {
        formData.append("file_name", paymentFile);
      }

      const endpoint = `/v1/door-management/user/${userIdVal}/cart/checkout/confirm`;
      const res: any = await WebService.postAPI({ action: endpoint, body: formData });

      toast.success(
        res?.data?.placed_on_credit
          ? "Order placed on credit."
          : "Payment uploaded successfully!"
      );
      setTimeout(() => onClose(), 1000);
    } catch (e: any) {
      toast.error(
        typeof e === "string"
          ? e
          : e?.message ||
              (selectedPay === "credit"
                ? "Credit payment failed. Please check your credit limit."
                : "Payment failed.")
      );
    }
    setCheckoutLoading(false);
  };

  const handleVariantModalClose = () => {
    setEditingCartItem(null);
    if (fields.customer?.value) fetchCartByUserId(fields.customer.value);
  };

  // global field errors (top form)
  const errors: { [k: string]: string } = {};
  if (submitAttempt && !fields.customer) errors.customer = "Select customer";
  if (submitAttempt && !fields.phone.trim()) errors.phone = "Phone number required";

  if (!open) return null;

  // --- RENDER ---
  return (
    <>
      <div
        className={`${styles.overlay} ${open ? styles.open : ""}`}
        onClick={handleOverlayClick}
        tabIndex={-1}
        aria-modal="true"
      >
        <div
          className={`${styles.panel} ${open ? styles.slideIn : ""}`}
          ref={panelRef}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top Header */}
          <div className={styles.topHeader}>
            <h2 className={styles.heading}>{isEditMode ? "Edit Order" : isExistingOrderPay ? `Confirm Order #${saleOrderId}` : "Add New Order"}</h2>
            <CrossButton onClick={onClose} className={styles.closeBtn} />
          </div>

          {/* Customer Form */}
          <form
            className={styles.form}
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              setSubmitAttempt(true);
            }}
          >
            <div className={styles.gridRow}>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  Customer Name <span style={{ color: "#E13D22" }}>*</span>
                </label>

                {openedFromProceedToPay ? (
                  <input className={styles.input} value={fields.customer?.label || ""} readOnly placeholder="Customer" />
                ) : (
                  <AsyncSelect
                    className={styles.selectDropdown}
                    classNamePrefix="select"
                    placeholder="Search customer..."
                    cacheOptions
                    defaultOptions
                    loadOptions={loadCustomerOptions}
                    value={fields.customer}
                    onChange={(opt) => setFields((f) => ({ ...f, customer: opt as CustomerOption }))}
                    noOptionsMessage={() => "No customer found"}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: 44,
                        borderRadius: 7,
                        borderColor: errors.customer ? "#F04848" : "#e4e4e4",
                        fontSize: 15,
                      }),
                    }}
                    isDisabled={isEditMode}
                  />
                )}

                {errors.customer && <div className={styles.errorMsg}>{errors.customer}</div>}
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  Phone Number <span style={{ color: "#E13D22" }}>*</span>
                </label>
                <input
                  className={styles.input}
                  placeholder="Phone number"
                  value={fields.phone}
                  onChange={(e) => {
                    setFields((f) => ({ ...f, phone: e.target.value }));
                    invalidateConfirmation();
                  }}
                  disabled={!!fields.customer}
                  style={{
                    borderColor: submitAttempt && !fields.phone.trim() ? "#F04848" : undefined,
                  }}
                />
                {errors.phone && <div className={styles.errorMsg}>{errors.phone}</div>}
              </div>

              {/* <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  Email <span className={styles.optional}>(optional)</span>
                </label>
                <input
                  className={styles.input}
                  placeholder="customer@email.com"
                  value={fields.email}
                  onChange={(e) => {
                    setFields((f) => ({ ...f, email: e.target.value }));
                    invalidateConfirmation();
                  }}
                  disabled={!!fields.customer && !!fields.customer.customer.email}
                />
              </div> */}
            </div>
          </form>
            {/* Customer Meta & Actions (Add More above Refresh) */}
            <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 7 }}>
              {fields.customer && (
                <>
                  <div className={styles.customerMeta}>
                  { /* <div>Email: {fields.customer.customer.email || "—"}</div>*/}
                    <div>Phone: {fields.customer.customer.mobile_number || "—"}</div>
                    {fields.customer?.customer?.is_credit_allowed &&
                      typeof fields.customer.customer.credit_limit === "number" &&
                      fields.customer.customer.credit_limit > 0 && (
                        <div style={{ color: "#2b6a1b", fontWeight: 600 }}>
                          Credit Limit: ₹{fields.customer.customer.credit_limit.toLocaleString()}
                        </div>
                      )}
                  </div>
                  {/* right aligned; stacked vertically */}
                  {!isExistingOrderPay && (
                  <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                    <button
                      className={styles.refreshBtn}
                      onClick={() => window.open(SHOP_URL, "_blank", "noopener,noreferrer")}
                      title="Open Shop in a new tab to add more items"
                    >
                      <FaPlus style={{ marginRight: 6 }} />
                      Add More
                    </button>
                    <button
                      className={styles.refreshBtn}
                      onClick={() => {
                        if (fields.customer) {
                          fetchCartByUserId(fields.customer.value);
                          fetchAddresses(fields.customer.customer.user_id);
                        }
                      }}
                      title="Refresh Cart"
                    >
                      <FaSyncAlt style={{ marginRight: 6 }} />
                      Refresh Cart
                    </button>
                  </div>
                  )}
                </>
              )}
            </div>
          {/* Cart */}
          <div className={styles.sectionHeader}>{isExistingOrderPay ? "Order Items" : "My Cart"}</div>
          <div className={styles.cartBox}>
            {cartLoading ? (
              <div>Loading...</div>
            ) : cart.length === 0 ? (
              <div className={styles.emptyCart}>{isExistingOrderPay ? "No items in this order" : "Cart is empty"}</div>
            ) : (
              cart.map((item) => {
                const productTotal = cartLineProductTotal(item);
                const deliveryTotal = cartLineDelivery(item);
                const packagingTotal = cartLinePackaging(item);
                return (
                  <div className={styles.cartItem} key={item.cart_item_id}>
                    <img
                      className={styles.productImg}
                      src={getCartItemImgSrc(item)}
                      alt={item.product?.name || item.product_name || item.name || "Product"}
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        img.onerror = null;
                        img.src = PLACEHOLDER_IMG;
                      }}
                    />

                    <div className={styles.cartItemInfo}>
                      <div className={styles.itemTitle}>
                        {item.product?.name || item.product_name || item.name || "Product"}
                      </div>
                      {(() => {
                        const orderSize = getOrderSizeLabel(item);
                        const billSize = getBillSizeLabel(item);
                        const hasColor = !!(item.color && String(item.color).trim());
                        if (!orderSize && !billSize && !hasColor) return null;
                        return (
                          <>
                            {orderSize ? (
                              <div className={styles.itemMeta}>Order Size: {orderSize}</div>
                            ) : null}
                            {billSize ? (
                              <div className={styles.itemMeta}>Bill Size: {billSize}</div>
                            ) : null}
                            {hasColor ? (
                              <div className={styles.itemMeta}>{item.color}</div>
                            ) : null}
                          </>
                        );
                      })()}

                      <div className={styles.itemBreakdown}>
                        <div>
                          Product: <b>₹{productTotal.toLocaleString()}</b>
                        </div>
                        <div>
                          Packaging: <b>₹{packagingTotal.toLocaleString()}</b>
                        </div>
                        <div>
                          Delivery: <b>₹{deliveryTotal.toLocaleString()}</b>
                        </div>
                      </div>
                    </div>
                    <div className={styles.cartItemActions}>
                      <div className={styles.price}>₹{productTotal.toLocaleString()}</div>
                      {isExistingOrderPay ? (
                        <span className={styles.qtyValue}>Qty: {item.quantity}</span>
                      ) : (
                      <>
                      <button
                        className={styles.qtyBtn}
                        onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        -
                      </button>
                      <span className={styles.qtyValue}>{item.quantity}</span>
                      <button className={styles.qtyBtn} onClick={() => handleUpdateQuantity(item, item.quantity + 1)}>
                        +
                      </button>
                      <FaEdit
                        className={styles.editIcon}
                        onClick={() => setEditingCartItem(item)}
                        title="Edit"
                        style={{ marginRight: 7, cursor: "pointer" }}
                      />
                      <FaTrashAlt
                        className={styles.trashIcon}
                        onClick={() => handleDeleteCartItem(item.cart_item_id)}
                        title="Delete"
                        style={{ marginLeft: 4 }}
                      />
                      </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {cart.length > 0 && (
              <div className={styles.paymentSummary}>
                <div className={styles.summaryRow}>
                  <span>Sub Total Product</span>
                  <span>₹{summary.subtotal.toLocaleString()}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>Total Delivery</span>
                  <span>₹{summary.delivery.toLocaleString()}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>Total Packaging</span>
                  <span>₹{summary.packaging.toLocaleString()}</span>
                </div>
                {isExistingOrderPay && saleOrderId ? (
                  <OrderChargesEditor
                    saleOrderId={Number(saleOrderId)}
                    charges={orderCharges}
                    editable
                    onSaved={loadExistingOrder}
                  />
                ) : !isEditMode && (
                  <OrderChargesEditor
                    saleOrderId={orderLoaded && orderId ? Number(orderId) : null}
                    charges={orderLoaded ? orderCharges : draftCharges}
                    editable
                    onSaved={() => orderId && loadOrder(orderId, false)}
                    onDraftChange={setDraftCharges}
                  />
                )}
                {orderLoaded && orderExtras.otherCharges !== 0 && (
                  <div className={styles.summaryRow}>
                    <span>Door Charges</span>
                    <span>
                      {orderExtras.otherCharges < 0 ? "−" : ""}₹{Math.abs(orderExtras.otherCharges).toLocaleString()}
                    </span>
                  </div>
                )}
                {orderLoaded && orderExtras.discount > 0 && (
                  <div className={styles.summaryRow}>
                    <span>Discount</span>
                    <span>−₹{orderExtras.discount.toLocaleString()}</span>
                  </div>
                )}
                <hr />
                <div className={styles.summaryRow} style={{ fontWeight: 700 }}>
                  <span>Total</span>
                  <span>
                    ₹
                    {(orderLoaded
                      ? summary.total
                      : summary.total + draftCharges.reduce((s, c) => s + (Number(c.amount) || 0), 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Billing Address Section */}
          <div className={styles.sectionHeader}>Billing Address</div>

          {/* Saved addresses (billing) */}
          <div className={styles.addressList}>
            {addresses.length === 0 ? (
              <div style={{ color: "#999", padding: "6px 0" }}>
                No saved addresses found for this customer. Add a shipping address below to enable Confirm.
              </div>
            ) : (
              addresses.map((addr) => (
                <div className={styles.addrCard} key={addr.address_id}>
                  <input
                    type="radio"
                    checked={Number(selectedAddress) === Number(addr.address_id)}
                    onChange={() => {
                      setSelectedAddress(addr.address_id);
                      invalidateConfirmation();
                    }}
                  />
                  <div>
                    <div className={styles.addrName}></div>
                    <div>{addr.complete_address || addr.address}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          {addresses.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0 10px 0" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={sameAsBilling}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setSameAsBilling(checked);
                  setCustomSaved(false);
                  setIsEditingShipping(!checked);
                  invalidateConfirmation();
                }}
              />
              Delivery address same as Billing address
            </label>
          </div>
          )}

          {/* If not same, or customer has no saved address -> show Shipping Address form */}
          {(!sameAsBilling || addresses.length === 0) && (
            <>
              {isEditingShipping ? (
                <form className={styles.addAddrForm} onSubmit={handleSaveCustomAddress} style={{ marginTop: 6 }}>
                  <h4>Shipping Address</h4>

                  {/* Name */}
                  <input
                    placeholder="Consigne Name"
                    value={addForm.name}
                    onChange={(e) => {
                      setAddForm((f) => ({ ...f, name: e.target.value }));
                      invalidateConfirmation();
                    }}
                    style={{
                      borderColor: addressSubmitAttempt && fieldError(addForm.name) ? "#F04848" : undefined,
                    }}
                  />
                  {addressSubmitAttempt && fieldError(addForm.name) && (
                    <div className={styles.errorMsg}>Name is required</div>
                  )}

                  {/* Mobile */}
                  <input
                    placeholder=" Consige Mobile Number"
                    value={addForm.mobile_number}
                    onChange={(e) => {
                      setAddForm((f) => ({ ...f, mobile_number: e.target.value }));
                      invalidateConfirmation();
                    }}
                    style={{
                      borderColor:
                        addressSubmitAttempt && !mobileValid(addForm.mobile_number) ? "#F04848" : undefined,
                    }}
                  />
                  {addressSubmitAttempt && !mobileValid(addForm.mobile_number) && (
                    <div className={styles.errorMsg}>Valid mobile number is required</div>
                  )}

                  {/* Country */}
                  <select
                    value={addForm.country}
                    onChange={(e) => {
                      setAddForm((f) => ({ ...f, country: e.target.value }));
                      invalidateConfirmation();
                    }}
                    style={{
                      borderColor: addressSubmitAttempt && fieldError(addForm.country) ? "#F04848" : undefined,
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.id || c.name} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {addressSubmitAttempt && fieldError(addForm.country) && (
                    <div className={styles.errorMsg}>Country is required</div>
                  )}

                  {/* State */}
                  <select
                    value={addForm.state}
                    onChange={(e) => {
                      setAddForm((f) => ({ ...f, state: e.target.value }));
                      invalidateConfirmation();
                    }}
                    disabled={!addForm.country}
                    style={{
                      borderColor: addressSubmitAttempt && fieldError(addForm.state) ? "#F04848" : undefined,
                    }}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.id || s.name} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  {addressSubmitAttempt && fieldError(addForm.state) && (
                    <div className={styles.errorMsg}>State is required</div>
                  )}

                  {/* City */}
                  <select
                    value={addForm.city}
                    onChange={(e) => {
                      setAddForm((f) => ({ ...f, city: e.target.value }));
                      invalidateConfirmation();
                    }}
                    disabled={!addForm.state}
                    style={{
                      borderColor: addressSubmitAttempt && fieldError(addForm.city) ? "#F04848" : undefined,
                    }}
                  >
                    <option value="">Select City</option>
                    {cities.map((c) => (
                      <option key={c.id || c.name} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {addressSubmitAttempt && fieldError(addForm.city) && (
                    <div className={styles.errorMsg}>City is required</div>
                  )}

                  {/* Address */}
                  <input
                    placeholder="Address"
                    value={addForm.address}
                    onChange={(e) => {
                      setAddForm((a) => ({ ...a, address: e.target.value }));
                      invalidateConfirmation();
                    }}
                    style={{
                      borderColor: addressSubmitAttempt && fieldError(addForm.address) ? "#F04848" : undefined,
                    }}
                  />
                  {addressSubmitAttempt && fieldError(addForm.address) && (
                    <div className={styles.errorMsg}>Address is required</div>
                  )}

                  {/* Landmark */}
                  <input
                    placeholder="Landmark"
                    value={addForm.landmark}
                    onChange={(e) => {
                      setAddForm((a) => ({ ...a, landmark: e.target.value }));
                      invalidateConfirmation();
                    }}
                    style={{
                      borderColor: addressSubmitAttempt && fieldError(addForm.landmark) ? "#F04848" : undefined,
                    }}
                  />
                  {addressSubmitAttempt && fieldError(addForm.landmark) && (
                    <div className={styles.errorMsg}>Landmark is required</div>
                  )}

                  <div style={{ display: "flex", gap: 10 }}>
                    <button type="submit" className={styles.saveAddrBtn} disabled={savingAddress}>
                      {savingAddress ? "Saving..." : customSaved ? "Saved ✓" : "Save"}
                    </button>
                    <button
                      type="button"
                      className={styles.saveAddrBtn}
                      style={{ background: "#eee", color: "#333" }}
                      onClick={() => {
                        if (customSaved) setIsEditingShipping(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                // Preview (view mode)
                <div
                  className={styles.addrPreview}
                  style={{
                    marginTop: 10,
                    border: "1px solid #eaeaea",
                    borderRadius: 8,
                    padding: "12px 14px",
                    background: "#fafafa",
                    display: "grid",
                    gap: 4,
                  }}
                  aria-label="Shipping address preview"
                >
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Shipping Address {customSaved ? <span style={{ color: "#2b6a1b" }}>• Saved</span> : null}
                  </div>

                  {/* Name • Mobile */}
                  <div>
                    {(addForm.name || "—")}
                    {addForm.mobile_number ? ` • ${addForm.mobile_number}` : ""}
                  </div>

                  {/* Address */}
                  <div>{addForm.address || "—"}</div>

                  {/* Landmark (if any) */}
                  {addForm.landmark ? <div>Landmark: {addForm.landmark}</div> : null}

                  {/* City, State, Country */}
                  <div>
                    {[
                      (cities.find((c: any) => String(c.id) === String(addForm.city))?.name) || "",
                      (states.find((s: any) => String(s.id) === String(addForm.state))?.name) || "",
                      (countries.find((x: any) => String(x.id) === String(addForm.country))?.name) || "",
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className={styles.saveAddrBtn}
                      onClick={() => setIsEditingShipping(true)}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Notes */}
          <div className={styles.sectionHeader}>Add notes</div>
          <input
            className={styles.notesInput}
            placeholder="Add notes"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              invalidateConfirmation();
            }}
            maxLength={200}
          />

          {/* Confirm */}
          {cart.length > 0 && (!hasConfirmed || orderDirty) && (
            <button
              type="button"
              className={styles.confirmBtn}
              onClick={handleConfirmOrder}
              style={{ margin: "26px 0" }}
              disabled={!canConfirmOrder}
            >
              Confirm
            </button>
          )}

          {/* Download Estimate */}
          {hasConfirmed && !orderDirty && orderId && cart.length > 0 && (
            <button
              className={styles.downloadEstimateBtn}
              onClick={handleDownloadEstimate}
              style={{ margin: "18px 0" }}
              disabled={downloading}
              title="Download the official estimate PDF for this sale order"
            >
              {downloading ? "Downloading..." : "Download Estimate PDF"}
            </button>
          )}

          {/* Payment */}
          {hasConfirmed && !orderDirty && cart.length > 0 && (
            <div className={styles.paymentRow}>
              <div className={styles.paymentMethodBox}>
                <h3>Payment Method</h3>
                <button
                  className={`${styles.payOptBtn} ${selectedPay === "bank" ? styles.paySelected : ""}`}
                  onClick={() => setSelectedPay("bank")}
                >
                  🏦 Bank Transfer
                </button>
                <button
                  className={`${styles.payOptBtn} ${selectedPay === "qr" ? styles.paySelected : ""}`}
                  onClick={() => setSelectedPay("qr")}
                >
                  📱 Scan QR Code
                </button>
                <button
                  className={`${styles.payOptBtn} ${selectedPay === "cash" ? styles.paySelected : ""}`}
                  onClick={() => setSelectedPay("cash")}
                >
                  💵 Cash
                </button>
                {fields.customer?.customer?.is_credit_allowed &&
                  typeof fields.customer.customer.credit_limit === "number" &&
                  fields.customer.customer.credit_limit > 0 && (
                    <button
                      className={`${styles.payOptBtn} ${selectedPay === "credit" ? styles.paySelected : ""}`}
                      onClick={() => setSelectedPay("credit")}
                    >
                      💳 Use Credit
                      <span style={{ fontSize: 13, color: "#7b7700", fontWeight: 600, marginLeft: 9 }}>
                        (Initial Credit Limit: ₹{fields.customer.customer.credit_limit.toLocaleString()})
                      </span>
                    </button>
                  )}
              </div>

              <div className={styles.paymentDetailBox}>
                <h3>Payment Details</h3>

                {selectedPay === "bank" && (
                  <div className={styles.bankDetails}>
                    <div>
                      <b>Bank holder name:</b> {HARDCODED_BANK_DETAILS.holder}
                    </div>
                    <div>
                      <b>Bank Name:</b> {HARDCODED_BANK_DETAILS.bank}
                    </div>
                    <div>
                      <b>Account Number:</b> {HARDCODED_BANK_DETAILS.acc}
                    </div>
                    <div>
                      <b>IFSC Code:</b> {HARDCODED_BANK_DETAILS.ifsc}
                    </div>
                    <div>
                      <b>Address:</b> {HARDCODED_BANK_DETAILS.addr}
                    </div>
                  </div>
                )}

                {selectedPay === "qr" && (
                  <div className={styles.qrSection}>
                    <img src={HARDCODED_QR} alt="QR Code" className={styles.qrImg} />
                    <div>Scan this QR to pay with any UPI App</div>
                  </div>
                )}

                {(selectedPay === "qr" || selectedPay === "bank") && (
                  <>
                    <div className={styles.paymentUpload}>
                      <label>
                        <b>Upload Payment screenshot</b>
                        <input
                          type="file"
                          ref={fileInput}
                          style={{ display: "none" }}
                          accept="image/*,.pdf"
                          onChange={(e) => setPaymentFile(e.target.files?.[0] || null)}
                        />
                        <div className={styles.uploadBox} onClick={() => fileInput.current?.click()}>
                          {paymentFile ? paymentFile.name : <span>Click or drag to upload</span>}
                        </div>
                      </label>
                    </div>

                    {selectedPay === "bank" && (
                      <div className={styles.utrBox}>
                        <label>
                          <b>Enter UTR number</b>
                        </label>
                        <input
                          className={styles.utrInput}
                          placeholder="UTR Number"
                          value={utr}
                          onChange={(e) => setUTR(e.target.value)}
                        />
                      </div>
                    )}

                    <button className={styles.checkoutBtn} onClick={handlePaymentCheckout} disabled={checkoutLoading}>
                      {checkoutLoading ? "Processing..." : `Checkout & Pay ₹${summary.total}`}
                    </button>
                  </>
                )}

                {selectedPay === "cash" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <label>
                      <b>Enter Cash Amount</b>
                    </label>
                    <input
                      className={styles.utrInput}
                      type="number"
                      min={1}
                      max={summary.total}
                      placeholder={`Enter amount (max ₹${summary.total})`}
                      value={cashAmount}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (parseInt(val) > summary.total) val = summary.total.toString();
                        setCashAmount(val);
                      }}
                      required
                    />
                    <button
                      className={styles.checkoutBtn}
                      onClick={async () => {
                        if (cashAmount && Number(cashAmount) < summary.total) {
                          const result = await Swal.fire({
                            icon: "warning",
                            title: "Partial Payment!",
                            html: `
                              <div style="margin-bottom:8px;">
                                You are paying only <b>₹${cashAmount}</b>.<br/>
                                <span style="color:#c77d00;">₹${summary.total - Number(cashAmount)} will remain due!</span>
                              </div>
                              Do you want to continue?
                            `,
                            showCancelButton: true,
                            confirmButtonText: "Yes, continue",
                            cancelButtonText: "Cancel",
                            heightAuto: false,
                          });
                          if (!result.isConfirmed) return;
                        }
                        handlePaymentCheckout();
                      }}
                      disabled={checkoutLoading || !cashAmount}
                    >
                      {checkoutLoading ? "Processing..." : `Confirm Cash Payment`}
                    </button>
                  </div>
                )}

                {selectedPay === "credit" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div
                      style={{
                        background: "#fffbe7",
                        border: "1.5px solid #ffe066",
                        borderRadius: 9,
                        padding: "14px 18px",
                        marginBottom: 6,
                        fontWeight: 600,
                        color: "#7b7700",
                      }}
                    >
                      Initial Credit Limit: ₹
                      {typeof fields.customer?.customer?.credit_limit === "number"
                        ? fields.customer.customer.credit_limit.toLocaleString()
                        : "0"}
                    </div>

                    <button className={styles.checkoutBtn} onClick={handlePaymentCheckout} disabled={checkoutLoading}>
                      {checkoutLoading ? "Processing..." : `Checkout with Credit`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Variant Modal */}
      {editingCartItem && (
        <ChooseVariantModal
          onClose={handleVariantModalClose}
          cartItem={{
            ...editingCartItem,
            PRODUCT: {
              id: editingCartItem.product_id,
              name:
                editingCartItem.product?.product_name ??
                editingCartItem.product?.name ??
                editingCartItem.product_name ??
                "",
              description:
                ("design_description" in (editingCartItem.product || {}) &&
                  (editingCartItem.product as any).design_description) ||
                editingCartItem.product?.product_description ||
                editingCartItem.product?.description ||
                editingCartItem.product_description ||
                "",
              images: editingCartItem.product?.images ?? [],
              sku_id: "",
              rating: 0,
              review_count: 0,
            },
            product_name:
              editingCartItem.product?.product_name ??
              editingCartItem.product?.name ??
              editingCartItem.product_name ??
              "",
            product_description:
              ("design_description" in (editingCartItem.product || {}) &&
                (editingCartItem.product as any).design_description) ||
              editingCartItem.product?.product_description ||
              editingCartItem.product?.description ||
              editingCartItem.product_description ||
              "",
          }}
          onUpdated={() => {
            setEditingCartItem(null);
            if (fields.customer?.value) fetchCartByUserId(fields.customer.value);
            invalidateConfirmation(); // any variant change makes order dirty
          }}
          itemType={editingCartItem.product_type === "RAW_MATERIAL" ? "RAW_MATERIAL" : "PRODUCT"}
          customerId={fields.customer?.value}
        />
      )}
    </>
  );
};

export default AddSaleOrderPanel;

